/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import Datepicker from 'react-tailwindcss-datepicker';
import { useMessageBar } from 'hooks/useMessageBar';
import { Button, Checkbox } from '@mui/material';
import {
  findProjectById,
  getIssueSetupList,
  getProjectIssueCnt,
} from 'apis/setup/issue/IssueSetup';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { PageMode } from 'models/common/Common';
import { IssueSetup, SearchParam } from 'models/setup/issue/issue';
import useSessionStore from 'stores/useSessionStore';
import { st } from 'components/inputs/CustomInput';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { useLoading } from 'components/process/Loading';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { DatePickerWrap } from 'components/inputs/DatePicker';
import dayjs from 'dayjs';
import { CommonUtil } from 'utils/CommonUtil';
import ProjectIssueCreatePage from './ProjectIssueRegisterPage';
import _ from 'lodash';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import { findDisplayProject } from 'apis/setup/SetupProject';

/**
 * @description 셋업이슈 등록/조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  26.05.21    jihwan89      그리드 핀 고정 설비명까지 추가
 */

interface RegisterParam {
  mode: PageMode;
  isuId?: string;
  elmPrjId?: string;
  excpId?: string;
  procChkGrCd?: string;
  procChkCd?: string;
}

const ProjectIssueSetupPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { userId, langCd, elmPrjId, deptCd, deptNm, ensolYn } = useSessionStore();
  const [searchParams] = useSearchParams();
  const locationState = useLocation();
  const [locationParam, setLocationParam] = useState<RegisterParam>({ ...locationState.state });
  const [projectCnt, setProjectCnt] = useState<IssueSetup>();
  const [errors, setErrors] = useState<any>({});

  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  const initSearchParam = {
    bildPlntCds: [],
    elmPrjId: locationParam?.elmPrjId || elmPrjId || '',
    tgtEqpId: '',
    eqpNm: '',
    isuTpCd: '',
    isuTitNm: '',
    isuStatCd: '',
    startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
    excpId: ensolYn === 'Y' ? locationParam?.excpId || '' : deptCd,
    excpNm: ensolYn === 'Y' ? '' : deptNm,
    procChkCd: locationParam?.procChkCd || '',
  };

  const initDefaultSearchParam = {
    bildPlntCds: [],
    elmPrjId: '',
    tgtEqpId: '',
    eqpNm: '',
    isuTpCd: '',
    isuTitNm: '',
    isuStatCd: '',
    startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
    excpId: ensolYn === 'Y' ? '' : deptCd,
    excpNm: ensolYn === 'Y' ? '' : deptNm,
    procChkCd: '',
  };

  const [searchParam, setSearchParam] = useState<SearchParam>({ ...initSearchParam });
  const [registerParam, setRegisterParam] = useState<RegisterParam>();

  const [flexRef, setFlexRef] = useState<any>();
  const [rowData, setRowData] = useState<IssueSetup[]>([]);
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [isuStatCd, setIsuStatCd] = useState<Code[]>([]);
  const [isuTpCd, setIsuTpCd] = useState<Code[]>([]);
  const [isuOccrCd, setIsuOccrCd] = useState<Code[]>([]);
  const [filteredIsuOccrCd, setFilteredIsuOccrCd] = useState<Code[]>([]);
  const [isuLocCd, setIsuLocCd] = useState<Code[]>([]);
  const [filteredIsuLocCd, setFilteredIsuLocCd] = useState<Code[]>([]);
  const [isuTpLvCd, setIsuTpLvCd] = useState<Code[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<IssueSetup[]>([]);
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]); //협력사
  const [openIssuePopup, setOpenIssuePopup] = useState<boolean>(false);

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 60,
      isReadOnly: true,
      cellTemplate: (grid) => grid.row._idx + 1,
      align: 'center',
    },
    {
      binding: 'bildPlntNm',
      header: String(t('com.label.플랜트', '플랜트')),
      width: 100,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'elmPrjNM',
      header: String(t('com.label.프로젝트명', '프로젝트명')),
      width: 240,
      isReadOnly: true,
      align: 'left',
    },
    {
      binding: 'eqpId',
      header: String(t('com.label.M-CODE', 'M-CODE')),
      width: 140,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'eqpNm',
      header: String(t('com.label.설비명', '설비명')),
      width: 240,
      isReadOnly: true,
      align: 'left',
    },
    {
      binding: 'isuTpNm',
      header: String(t('setup.label.분류', '분류')),
      width: 80,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'isuOccrDivsNm',
      header: String(t('setup.label.구분', '구분')),
      width: 80,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'isuOccrLocNm',
      header: String(t('setup.label.상세', '상세')),
      width: 80,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'isuTitNm',
      header: String(t('com.label.제목', '제목')),
      width: 240,
      isReadOnly: true,
      align: 'left',
    },
    {
      binding: 'isuStatNm',
      header: String(t('com.label.상태', '상태')),
      width: 80,
      isReadOnly: true,
      align: 'center',
      cssClass: 'WijmoTag',
      cellTemplate: (item) => {
        if (item.item.isuStatCd === 'PRJ_CPLT') {
          return `<span class="yellow">${item.value}</span>`;
        } else if (item.item.isuStatCd === 'REG_CPLT') {
          return `<span class="green">${item.value}</span>`;
        } else if (item.item.isuStatCd === 'SAVE') {
          return `<span class="blue">${item.value}</span>`;
        } else {
          return `<span class="grey">${item.value}</span>`;
        }
      },
    },
    {
      binding: 'excpNm',
      header: String(t('com.label.협력업체명', '협력업체명')),
      width: 160,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'cpltDt',
      header: String(t('com.label.완료일자', '완료일자')),
      width: 100,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'dataInsUserNm',
      header: String(t('com.label.등록자', '등록자')),
      width: 100,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'isuRegDt',
      header: String(t('com.label.발생일자', '발생일자')),
      width: 100,
      isReadOnly: true,
      align: 'center',
    },
  ];

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');
    const isuStatCd = await getCommonCodeNames('ISU_STAT_CD');
    const isuTpCd = await getCommonCodeNames('ISU_TP_CD');
    const isuOccrCd = await getCommonCodeNames('ISU_OCCR_DIVS_CD');
    const isuLocCd = await getCommonCodeNames('ISU_OCCR_LOC_CD');
    const isuTpLvCd = await getCommonCodeNames('ISU_TP_LV_CD');

    setBildPlntCode(bildPlntCode);
    setIsuStatCd(isuStatCd);
    setIsuTpCd(isuTpCd);
    setIsuOccrCd(isuOccrCd);
    setIsuLocCd(isuLocCd);
    setIsuTpLvCd(isuTpLvCd);

    findDisplayProject().then((result) => {
      if (result !== null) {
        setPrjMasterData(result);

        if (!searchParam?.elmPrjId && result?.length && result[0]?.elmPrjId) {
          handleOnChange('elmPrjId', result[0].elmPrjId);
          return;
        }
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const getIssueCnt = async () => {
    getProjectIssueCnt(searchParam)
      .then((result: IssueSetup) => {
        setProjectCnt(result);
      })
      .finally(() => openLoading(false));
  };

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.elmPrjId,
          cmnCdNm: cur.elmPrjNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjMasterData]);

  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  useEffect(() => {
    initCodes();
  }, []);

  const onInitialized = useEvent((grid) => {
    setFlexRef(grid);

    grid.hostElement.addEventListener('dblclick', (e) => {
      const ht = grid.hitTest(e);
      const row = ht.row;
      const item = grid.rows[row]?.dataItem; // 선택된 행 데이터
      if (ht.panel === grid.cells) {
        setRegisterParam({
          mode: PageMode.READ,
          isuId: item?.isuId,
        });
        setOpenIssuePopup(true);
      }
    });
  });

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      flexRef,
      t('ip.label.Issue List 등록/조회', 'Issue List 등록/조회'),
      t('ip.label.Issue List', 'Issue List')
    );
  };

  const btnSearch = () => {
    setErrors({
      elmPrjId: _.isEmpty(searchParam.elmPrjId),
    });

    if (_.isEmpty(searchParam.elmPrjId)) {
      return;
    }

    openLoading(true);
    getIssueCnt();
    getIssueSetupList(searchParam)
      .then((result: IssueSetup[]) => {
        if (result !== null) {
          setRowData(result);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const btnRefresh = () => {
    btnSearch();
  };

  const onUpdateGridData = (isuId = '') => {
    if (!isuId) return;

    /**
     * 이슈 등록/수정 팝업 종료 후
     * 해당 isuId 값의 데이터 조회하여 UI 업데이트
     */
    openLoading(true);
    findProjectById(isuId)
      .then((result: IssueSetup) => {
        if (result) {
          const view = flexRef.collectionView;
          view.deferUpdate(() => {
            view.sourceCollection.forEach((o: IssueSetup) => {
              if (o.isuId === result.isuId) {
                o.isuTpCd = result.isuTpCd; // 분류
                o.isuTpNm = result.isuTpNm;
                o.isuOccrDivsCd = result.isuOccrDivsCd; // 구분
                o.isuOccrDivsNm = result.isuOccrDivsNm;
                o.isuOccrLocCd = result.isuOccrLocCd; // 상세
                o.isuOccrLocNm = result.isuOccrLocNm;
                o.isuTitNm = result.isuTitNm; // 이슈명
                o.isuStatCd = result.isuStatCd; // 상태
                o.isuStatNm = result.isuStatNm;
                o.cpltDt = result.cpltDt; // 완료일자
                o.isuRegDt = result.isuRegDt; // 등록일자
              }
            });
          });
        }
      })
      .finally(() => openLoading(false));
  };

  useEffect(() => {
    if (searchParam?.elmPrjId) {
      findExcpByElmPrjId(searchParam?.elmPrjId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
    } else {
      setPrjExcpData([]);
    }
    if (searchParam?.elmPrjId) {
      btnSearch();
    }
  }, [searchParam?.elmPrjId, searchParam?.excpId]);

  useEffect(() => {
    if (location.pathname === '/setup/issue') {
      setLocationParam(locationState.state);
    }
  }, [locationState.key]);

  useEffect(() => {
    setSearchParam((prev) => ({ ...prev, ...locationParam }));
  }, [locationParam]);

  useEffect(() => {
    if (!searchParam?.elmPrjId) return;
    btnSearch();
  }, [searchParam?.elmPrjId]);

  useEffect(() => {
    if (!openIssuePopup && registerParam?.mode === PageMode.WRITE) {
      btnSearch();
    }
  }, [openIssuePopup]);

  useEffect(() => {
    const optIsuOccrCd = isuOccrCd.filter((d) =>
      isuTpLvCd.some((m) => m.optValCtn1 === searchParam.isuTpCd && m.optValCtn2 === d.cmnCd)
    );
    setFilteredIsuOccrCd(optIsuOccrCd);

    const optIsuLocCd = isuLocCd.filter((d) =>
      isuTpLvCd.some(
        (m) =>
          m.optValCtn1 === searchParam.isuTpCd &&
          m.optValCtn2 === searchParam.isuOccrDivsCd &&
          m.optValCtn3 === d.cmnCd
      )
    );
    setFilteredIsuLocCd(optIsuLocCd);
  }, [searchParam.isuTpCd, searchParam.isuOccrDivsCd, isuTpLvCd]);

  const btnReload = () => {
    setSearchParam({ ...initDefaultSearchParam });
  };

  const handleOnChange = (name: string, value: any) => {
    setSearchParam((prev) => ({ ...prev, [name]: value }));
  };

  const moveWritePage = () => {
    setRegisterParam({
      mode: PageMode.WRITE,
    });
    setOpenIssuePopup(true);
  };

  useEffect(() => {
    const isuId = searchParams?.get('isuId');
    if (isuId && window.location.pathname === '/setup/issue') {
      openLoading(true);
      setRegisterParam({
        mode: PageMode.READ,
        isuId: isuId,
      });
      setOpenIssuePopup(true);
    }
  }, [searchParams?.get('isuId')]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('admin.grid.플랜트', '플랜트')}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.bildPlntCds}
                  onChange={(value) => {
                    handleOnChange('bildPlntCd', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className={'dot'}>{t('admin.grid.프로젝트명', '프로젝트명')}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionsPrjMaster}
                  defaultValue={searchParam.elmPrjId}
                  onChange={(value) => {
                    setSearchParam(() => ({ ...searchParam, elmPrjId: value }));
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.M-CODE', 'M-CODE')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="eqpId"
                  placeholder={String(
                    t('setup.label.M-CODE를 입력해 주세요.', 'M-CODE를 입력해 주세요.')
                  )}
                  value={searchParam?.eqpId}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.설비명', '설비명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="eqpNm"
                  placeholder={String(
                    t('setup.label.설비명을 입력해 주세요.', '설비명을 입력해 주세요.')
                  )}
                  value={searchParam?.eqpNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('admin.grid.분류', '분류')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={isuTpCd}
                  defaultValue={searchParam?.isuTpCd}
                  onChange={(value) => {
                    setSearchParam(() => ({ ...searchParam, isuTpCd: value }));
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.구분', '구분')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={filteredIsuOccrCd}
                  defaultValue={searchParam?.isuOccrDivsCd}
                  onChange={(value) => {
                    setSearchParam(() => ({ ...searchParam, isuOccrDivsCd: value }));
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.상세', '상세')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={filteredIsuLocCd}
                  defaultValue={searchParam?.isuOccrLocCd}
                  onChange={(value) => {
                    setSearchParam(() => ({ ...searchParam, isuOccrLocCd: value }));
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.제목', '제목')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="isuTitNm"
                  placeholder={String(
                    t('setup.label.제목을 입력해 주세요.', '제목을 입력해 주세요.')
                  )}
                  value={searchParam?.isuTitNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('admin.grid.상태', '상태')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={isuStatCd}
                  defaultValue={searchParam?.isuStatCd}
                  onChange={(value) => {
                    setSearchParam(() => ({ ...searchParam, isuStatCd: value }));
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.발생일자', '발생일자')}</label>
                <DatePickerWrap className="range">
                  <Datepicker
                    inputClassName="datepickerInput"
                    value={{
                      startDate: searchParam?.startDate || null,
                      endDate: searchParam?.endDate || null,
                    }}
                    onChange={(value) => {
                      setSearchParam({
                        ...searchParam,
                        startDate: String(value?.startDate || ''),
                        endDate: String(value?.endDate || ''),
                      });
                    }}
                    i18n={langCd}
                    useRange={true}
                    placeholder="YYYY.MM.DD - YYYY.MM.DD"
                    displayFormat={'YYYY.MM.DD'}
                    maxDate={new Date()}
                  ></Datepicker>
                </DatePickerWrap>
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.협력업체명', '협력업체명')}</label>
                {!isEmailFormat() ? (
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={optionsPrjExcp}
                    defaultValue={searchParam?.excpId}
                    onChange={(value) => {
                      handleOnChange('excpId', value);
                    }}
                  />
                ) : (
                  <div className="txt">
                    <span>{searchParam?.excpNm}</span>
                  </div>
                )}
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.이슈완료 예정', '이슈완료 예정')}</label>
                <div className="txt">
                  <Checkbox
                    id={`proc-chk-chk-id`}
                    css={st.checkbox}
                    className="custom"
                    value={'ISU_EXCP'}
                    checked={searchParam.procChkCd === 'ISU_EXCP'}
                    onChange={(e, c) => {
                      handleOnChange('procChkCd', c ? e.target.value : '');
                    }}
                  />
                </div>
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button css={IconButton.button} className="reload" disableRipple onClick={btnReload} />
            <Button css={IconButton.button} className="find" disableRipple onClick={btnSearch}>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.이슈 목록', '이슈 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
          {projectCnt && <div className="info warning"> {projectCnt?.summary}</div>}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnRefresh} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isSelector={false}
        initialized={onInitialized}
        height={530}
        includePin={['bildPlntNm', 'elmPrjNM', 'eqpId', 'eqpNm']}
        frozenColumns={5}
      />
      <GlobalBtnGroup>
        <Button css={IconButton.button} className="create" onClick={moveWritePage} disableRipple>
          {t('com.button.이슈 등록', '이슈 등록')}
        </Button>
      </GlobalBtnGroup>
      {openIssuePopup && (
        <ProjectIssueCreatePage
          open={openIssuePopup}
          close={() => {
            onUpdateGridData(registerParam?.isuId);
            setOpenIssuePopup(false);
          }}
          onSearch={btnSearch}
          mode={registerParam?.mode || PageMode.READ}
          isuId={registerParam?.isuId || ''}
        />
      )}
    </>
  );
};

export default ProjectIssueSetupPage;
